import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from '@shared/customValidators';
import { UnsupportedMarketComponent } from '@shared/dialog-contents/unsupported-market/unsupported-market.component';
import { collectFormErrors } from '@shared/formHelpers';
import { Applicant, Application, ApplicationSegment, Company, CompanyType, CountryMetadata, GeolocationResult } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { ModalDialogService } from '@shared/services/modal-dialog.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

interface CompanySizeOption {
    label: string;
    value: number;
}

interface CountryOption extends CountryMetadata {
    unsupported: boolean;
    unsupportedReason?: 'not-active-market' | 'blacklisted';
}

type SignupFormGroup = {
    email: FormControl<string | null>;
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    country: FormControl<string | null>;
    companyName: FormControl<string | null>;
    companyType: FormControl<string | null>;
    companyTypeOther: FormControl<string | null>;
    companySize: FormControl<number | null>;
    companySizeFewerThan50: FormControl<number | null>;
    website: FormControl<string | null>;
};

@Component({
    selector: 'signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {
    private readonly subStepName = "Signup";
    private applicantToSave: Applicant;
    private companyToSave: Company;
    loading = false;
    form: FormGroup<SignupFormGroup>;
    countryOptions: CountryOption[] = [];
    companyTypesGroupedByCategory: Array<Array<CompanyType>> = [];
    companyTypesFlat: CompanyType[] = [];
    companyTypesLoading = false;
    fewerThan50Mode: boolean = null;

    private readonly fewerThan50Validator = (control: AbstractControl): ValidationErrors => {
        if (this.fewerThan50Mode) {
            return Validators.required(control);
        }
    };

    get hasCompanyTypes(): boolean {
        return this.companyTypesGroupedByCategory.length > 0;
    }

    readonly companySizeOptions: CompanySizeOption[] = [
        { label: "REGISTRATION.SIGNUP_STEP.SIGNUP.FEWER_THAN_50", value: 49 },
        { label: "50-99",    value: 99 },
        { label: "100-249",  value: 249 },
        { label: "250-499",  value: 499 },
        { label: "500-999",  value: 999 },
        { label: "1000-1999", value: 1999 },
        { label: "2000-4999", value: 4999 },
        { label: "5000+",    value: 5001 }
    ];

    constructor(
        private readonly fb: FormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly countryService: CountryService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly destroyRef: DestroyRef,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService,
        private readonly modalDialogService: ModalDialogService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const application: Application = this.route.snapshot.data.application;
        const geolocation: GeolocationResult = this.route.snapshot.data.geoLocationCountry;

        this.applicantToSave = { ...application.applicant };
        this.companyToSave = RegistrationHelper.cloneCompany(application.company ?? {} as Company);

        const existingEmployees = application.company?.employees;
        this.fewerThan50Mode = existingEmployees > 0 && existingEmployees < 50;
        const existingCompanySize = this.fewerThan50Mode ? 49 : (existingEmployees ?? null);
        const existingCompanySizeFewerThan50 = this.fewerThan50Mode ? existingEmployees : null;
        this.buildCountryOptions(application);
        const geolocatedSupported = this.countryOptions.find(c => c.iso3 === geolocation?.countryIso3)?.unsupported === false;
        const initialCountry = application.company?.registeredAddress?.countryISO ?? (geolocatedSupported ? geolocation.countryIso3 : null);

        const companySizeFewerThan50Validators = [
            Validators.pattern(/^\d*$/),
            Validators.min(1),
            Validators.max(49)
        ];
        const companySizeFewerThan50FormatValidator = (control: AbstractControl): ValidationErrors => {
            return companySizeFewerThan50Validators.reduce((error, validator) => error || validator(control), null);
        };

        this.form = this.fb.group({
            email: [application.applicant?.email],
            firstName: [application.applicant?.firstName, [Validators.required, Validators.maxLength(CustomValidators.getContactMaxlength('firstName'))]],
            lastName: [application.applicant?.lastName, [Validators.required, Validators.maxLength(CustomValidators.getContactMaxlength('lastName'))]],
            country: [initialCountry, Validators.required],
            companyName: [application.company?.registeredName, [Validators.required, Validators.maxLength(CustomValidators.getCompanyMaxlength('registeredName'))]],
            companyType: [{ value: application.company?.type ?? null, disabled: !initialCountry }, [Validators.required]],
            companyTypeOther: [{ value: application.company?.typeDescription ?? null, disabled: !initialCountry },
                [Validators.required, Validators.maxLength(CustomValidators.getCompanyMaxlength('typeDescription'))]],
            companySize: [existingCompanySize, Validators.required],
            companySizeFewerThan50: [existingCompanySizeFewerThan50, [this.fewerThan50Validator, companySizeFewerThan50FormatValidator]],
            website: [application.company?.website ?? null, [CustomValidators.urlValidator()]]
        });

        this.trackGeolocation(geolocation);
        this.form.controls.country.valueChanges.subscribe(iso3 => this.onCountryChange(iso3));
        this.form.controls.companySize.valueChanges.subscribe(value => {
            this.fewerThan50Mode = value === 49;
            if (value !== 49) {
                this.form.controls.companySizeFewerThan50.setValue(null);
            }
            this.form.controls.companySizeFewerThan50.updateValueAndValidity();
        });
        if (initialCountry) {
            this.onCountryChange(initialCountry, false);
        }
    }

    selectOnFocus(event: Event) {
        (event.target as HTMLInputElement).select();
    }

    private buildCountryOptions(application: Application) {
        const isDirectSegment = application.segment === ApplicationSegment.direct;
        const mainMarkets = this.countryService.getMainMarkets();

        this.initializeCountryList(this.route.snapshot.data.countryList, isDirectSegment, mainMarkets);
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');

            this.initializeCountryList(await this.countryService.getCurrentCountries(), isDirectSegment, mainMarkets);

            this.loadingScreenService.hide();
        });
    }

    private toCountryOption(c: CountryMetadata, isDirectSegment: boolean): CountryOption {
        const notActiveMarket = !isDirectSegment && !c.activeMarket;
        let unsupportedReason = undefined;
        if (c.blacklist) {
            unsupportedReason = 'blacklisted';
        } else if (notActiveMarket) {
            unsupportedReason = 'not-active-market';
        }
        return {
            ...c,
            unsupported: c.blacklist || notActiveMarket,
            unsupportedReason: unsupportedReason
        };
    }

    private initializeCountryList(allCountries: CountryMetadata[], isDirectSegment: boolean, mainMarkets: string[]) {
        const sorted = allCountries
            .map(c => this.toCountryOption(c, isDirectSegment))
            .sort(this.countryService.sortFunction);

        const main = sorted.filter(c => mainMarkets.includes(c.iso3));
        const other = sorted.filter(c => !mainMarkets.includes(c.iso3));

        this.countryOptions = [
            ...main,
            ...other
        ];
    }

    private async onCountryChange(iso3: string, resetValue = true): Promise<void> {
        const selected = this.countryOptions.find(c => c.iso3 === iso3);
        if (selected?.unsupported) {
            const geolocatedCountryIso: string = (this.route.snapshot.data.geoLocationCountry as GeolocationResult)?.countryIso3;
            this.amplitudeService.trackUnsupportedCountrySelected(selected.iso3, selected.unsupportedReason, geolocatedCountryIso);
            this.modalDialogService.showModalDialog(UnsupportedMarketComponent, {
                title: 'MODAL_CONTENTS.UNSUPPORTED_MARKET.TITLE',
                dismissLabel: 'MODAL_CONTENTS.UNSUPPORTED_MARKET.BUTTONS.DISMISS'
            });
            this.form.controls.country.setValue(null);
            return;
        }

        this.companyTypesGroupedByCategory = [];
        this.companyTypesFlat = [];

        if (!iso3) {
            this.form.controls.companyType.disable();
            this.form.controls.companyTypeOther.disable();
            return;
        }

        this.companyTypesLoading = true;
        try {
            const types = await this.registrationService.getCompanyTypes(iso3);
            types.sort(this.sortByCompanyCategory());
            this.companyTypesFlat = types;
            for (const type of types) {
                const lastGroup = this.companyTypesGroupedByCategory[this.companyTypesGroupedByCategory.length - 1];
                if (type.category === lastGroup?.[0].category) {
                    lastGroup.push(type);
                } else {
                    this.companyTypesGroupedByCategory.push([type]);
                }
            }
        } catch (e) {
            this.logger.debug(`No company types available for country ${iso3}`);
        } finally {
            this.companyTypesLoading = false;
        }

        if (this.hasCompanyTypes) {
            this.form.controls.companyTypeOther.disable();
            this.form.controls.companyTypeOther.setValue(null);
            this.form.controls.companyType.enable();
            if (resetValue) { this.form.controls.companyType.setValue(null); }
        } else {
            this.form.controls.companyType.disable();
            this.form.controls.companyType.setValue(null);
            this.form.controls.companyTypeOther.enable();
            if (resetValue) { this.form.controls.companyTypeOther.setValue(null); }
        }
    }

    private sortByCompanyCategory(): (a: CompanyType, b: CompanyType) => number {
        const order = ['REGISTERED_BUSINESS', 'OTHER_ENTITIES'];
        return (a, b) => order.indexOf(a.category) - order.indexOf(b.category);
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate(['auth/email-verification']).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep(): Promise<void> {
        if (!this.isValid()) {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            return;
        }

        this.loading = true;
        const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);
        const formValues = this.form.value;

        try {
            this.applicantToSave.firstName = formValues.firstName;
            this.applicantToSave.lastName = formValues.lastName;
            await this.registrationService.saveApplicant(this.applicantToSave);

            this.companyToSave.registeredName = formValues.companyName;
            this.companyToSave.kybProviderId = null;
            this.companyToSave.registeredAddress = {
                ...(this.companyToSave.registeredAddress),
                countryISO: formValues.country
            };
            this.companyToSave.type = this.hasCompanyTypes ? this.form.controls.companyType.value : 'other';
            this.companyToSave.typeDescription = this.hasCompanyTypes ? null : this.form.controls.companyTypeOther.value;
            this.companyToSave.employees = Number(this.fewerThan50Mode ? formValues.companySizeFewerThan50 : formValues.companySize);
            this.companyToSave.website = formValues.website || null;

            await this.registrationService.saveCompany(this.companyToSave);

            this.trackSubStepCompleted();
            this.registrationService.completeStep(REGISTRATION_STEP_ID.SIGNUP);
            this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                this.dynatrace.leaveAction(submitActionId);
            });
        } catch (e) {
            this.logger.error('Signup save failed', e);
            this.loading = false;
            this.dynatrace.leaveAction(submitActionId);
        }
    }

    // must run after buildCountryOptions: membership in countryOptions is the market filter, since
    // initializeCountryList already applies the segment-aware activeMarket/blacklist rule
    private trackGeolocation(geolocation: GeolocationResult): void {
        const { countryIso3, outcome } = geolocation ?? { countryIso3: null, outcome: 'failed' as const };
        const supported = countryIso3 ? this.countryOptions.some(c => c.iso3 === countryIso3) : null;
        this.amplitudeService.trackGeolocationResolved(countryIso3, outcome, supported);
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
        if (!this.registrationService.isStepComplete(REGISTRATION_STEP_ID.SIGNUP)) {
            this.gtmService.trackSignedUp();
            this.amplitudeService.trackSignupCompleted(this.companyToSave);
        }
        this.amplitudeService.trackSignupCountryAccepted(this.companyToSave.registeredAddress?.countryISO);
        this.amplitudeService.updateCompanySearchUsageMetric(Boolean(this.companyToSave.kybProviderId));
    }
}
